import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import {
  api,
  type DocumentRow,
  type FolderRow,
  type ProjectRow,
} from "@/lib/api";
import { pollingIntervalForDocuments } from "@/hooks/queries/ingestStage";
import { qk } from "@/lib/queryKeys";

type ProjectDetails = ProjectRow & { documents: DocumentRow[]; folders: FolderRow[] };

export function useProject(
  projectId: string,
  initialProject?: ProjectDetails | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => api.projects.get(projectId),
    initialData: initialProject ?? undefined,
    initialDataUpdatedAt: initialProject ? Date.now() : undefined,
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => pollingIntervalForDocuments((query.state.data as ProjectDetails | undefined)?.documents),
  });
}

export function useSuspenseProject(projectId: string) {
  return useSuspenseQuery({
    queryKey: qk.project(projectId),
    queryFn: () => api.projects.get(projectId),
    refetchInterval: (query) => pollingIntervalForDocuments((query.state.data as ProjectDetails | undefined)?.documents),
  });
}

export function useSuspenseProjectChats(projectId: string) {
  return useSuspenseQuery({
    queryKey: qk.projectChats(projectId),
    queryFn: () => api.projects.chats(projectId),
  });
}

export function useInvalidateProject() {
  const qc = useQueryClient();
  return (projectId: string) =>
    qc.invalidateQueries({ queryKey: qk.project(projectId) });
}
